/**
 * Tax-Loss Harvesting Analytics API Client — Chunk 149
 *
 * Typed client for:
 *   GET /analytics/tax/harvesting-opportunities/:userId
 *   GET /analytics/tax/harvesting-summary/:userId
 */

import apiClient from './client';
import type { DisplayCurrencyMetadata } from './portfolio';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface HarvestingOpportunity {
  /** Database lot ID — used for acknowledge calls */
  lotId: number;
  symbol: string;
  /** Quantity of the asset in this lot */
  quantity: number;
  /** Total cost basis (quantity × cost_basis_per_unit) in USD */
  costBasisUsd: number;
  /** Current market value in USD */
  currentValueUsd: number;
  /** Unrealised loss in USD (negative number, e.g. −1000) */
  unrealisedLossUsd: number;
  /** True if the lot has been held for less than 365 days */
  shortTerm: boolean;
  /** True if the user bought the same asset within the last 30 days (wash-sale risk) */
  washSaleRisk: boolean;
  /** Number of days the lot has been held */
  holdingDays: number;
  /** ISO 8601 date string of acquisition */
  dateAcquired: string;
}

export interface HarvestingOpportunitiesResponse {
  userId: string;
  threshold: number;
  opportunities: HarvestingOpportunity[];
  count: number;
  displayOpportunities?: Array<{
    lotId: number;
    costBasis: number | null;
    currentValue: number | null;
    unrealisedLoss: number | null;
  }>;
}

export interface HarvestingSummary extends DisplayCurrencyMetadata {
  userId: string;
  /** Total harvestable loss in USD (negative) — sum of all qualifying lots */
  totalHarvestableLossUsd: number;
  /** Estimated tax saving if the user is in the 20 % bracket */
  estimatedSaving20pct: number;
  /** Estimated tax saving if the user is in the 37 % bracket */
  estimatedSaving37pct: number;
  /** Number of lots exceeding the $100 threshold */
  opportunityCount: number;
  displayValues?: {
    totalHarvestableLoss: number | null;
    estimatedSaving20pct: number | null;
    estimatedSaving37pct: number | null;
  };
}

export interface HarvestingOpportunitiesPayload extends DisplayCurrencyMetadata, HarvestingOpportunitiesResponse {}

// ─── API calls ────────────────────────────────────────────────────────────────

/**
 * Fetch tax-loss harvesting opportunities for a user.
 * Available during beta for any signed-in workspace tier.
 *
 * @param userId   UUID of the user
 * @param threshold  Minimum absolute loss in USD to include (default 100)
 */
export async function getHarvestingOpportunities(
  userId: string,
  threshold = 100,
): Promise<HarvestingOpportunitiesPayload> {
  const { data } = await apiClient.get<HarvestingOpportunitiesPayload>(
    `/analytics/tax/harvesting-opportunities/${userId}`,
    { params: { threshold: String(threshold) } },
  );
  return data;
}

/**
 * Fetch a summary of harvestable losses for the Sidebar badge and summary bar.
 * Available during beta so signed-in users can compare the full harvesting workflow.
 */
export async function getHarvestingSummary(userId: string): Promise<HarvestingSummary> {
  const { data } = await apiClient.get<HarvestingSummary>(
    `/analytics/tax/harvesting-summary/${userId}`,
  );
  return data;
}
